import gql from 'graphql-tag';

export const ERROR_FRAGMENT = gql`
  fragment ErrorResult on ErrorResult {
    errorCode
    message
  }
`;

/** 物流 */
export const LOGISTICS_FRAGMENT = gql`
  fragment Logistics on Logistics {
    id
    logisticCode
    company
    state
    logisticInfo {
      logisticCode
      shipperCode
      traces {
        acceptStation
        acceptTime
      }
      state
    }
    order {
      id
    }
    fulfillment {
      id
      method
    }
  }
`;

/** 订单 - 行 */
export const ORDER_LINES_FRAGMENT = gql`
  fragment OrderLine on OrderLine {
    id
    quantity
    totalQuantity
    unitPrice
    featuredAsset {
      source
    }
    merchantVoluntaryRefund
    productVariant {
      id
      sku
      price
      name
      options {
        code
        name
      }
      featuredAsset {
        source
      }
      isThereAnyStock
      customFields {
        virtualTargetType
      }
      product {
        id
        name
        description
        enabled
        customFields {
          isVipProduct
          unit
        }
        featuredAsset {
          id
          width
          height
          name
          preview
          source
          type
        }
        customFields {
          price
          markingPrice
          unit
          limitType
          limitCount
          putOnSaleType
          putOnSaleTime
        }
      }
    }
    customFields {
      purchasePattern
      isAvailableAfterSale
      afterSaleLine {
        id
        createdAt
        updatedAt
        quantity
        price
      }
      afterSale {
        id
        cause
        state
        mode
      }
    }
  }
`;

/** 订单 - 项 */
export const ORDER_FRAGMENT = gql`
  fragment Order on Order {
    id
    createdAt
    updatedAt
    orderPlacedAt
    code
    state
    total
    totalQuantity
    isPaymentRewardActivity
    customFields {
      periods
      deliveryInterval
      firstShippingDate
      isAvailableAfterSale
      timeoutPeriodToBePaid
      timeoutPeriodToBeReceived
      remark
      orderPromotionResult {
        promResult {
          orderTotalPrice
        }
      }
    }
    fulfillments {
      id
      createdAt
      method
    }
    lines {
      ...OrderLine
    }
    surcharges {
      id
      price
    }
    shipping
    shippingAddress {
      fullName
      company
      streetLine1
      streetLine2
      city
      province
      postalCode
      country
      countryCode
      phoneNumber
      customFields {
        district
      }
    }
  }
  ${ORDER_LINES_FRAGMENT}
`;

/** 优惠券 */
export const COUPON_FRAGMENT = gql`
  fragment Coupon on Coupon {
    __typename
    id
    createdAt
    state
    enable
    updatedAt
    name
    remarks
    type
    isShareable
    showLabelInCommodity
    preferentialContent {
      preferentialType
      minimum
      discount
      maximumOffer
      includingDiscountProducts
    }
    validityPeriod {
      type
      startTime
      endTime
      numberOfDays
    }
    totalQuantity
    applicableProduct {
      applicableType
      productIds
    }
    introduce
    promotion {
      id
    }
    activityContent
    activityTime
    enableSubscribeMessage
    whetherRestrictUsers
    memberPlanIds
  }
`;

/** 用户优惠券 */
export const USER_COUPON_FRAGMENT = gql`
  fragment UserCoupon on UserCoupon {
    id
    createdAt
    updatedAt
    claimAt
    useAt
    maturityAt
    maturityType
    isNew
    validFromAt
    coupon {
      ...Coupon
    }
    state
  }
  ${COUPON_FRAGMENT}
`;

/** 会员计划 - 不包含会员优惠券 */
export const NOT_COUPON_MEMBERSHIPPLAN_FRAGMENT = gql`
  fragment MembershipPlan on MembershipPlan {
    id
    createdAt
    updatedAt
    name
    bannerImg
    backgroundImage
    protocolUsage
    validityPeriod {
      type
      startTime
      endTime
      numberOfDays
    }
    price
    introduce
    customerServiceNumber
    state
    rightsDiscount {
      enable
      discountRate
      restrictedUse
    }
    rightsPoints {
      enable
      pointsMultiple
    }
    rightsCoupon {
      enable
      presentedCoupon {
        couponId
        presentedCount
      }
    }
  }
`;

/** 会员 */
export const MEMBER_FRAGMENT = gql`
  fragment Member on Member {
    id
    createdAt
    updatedAt
    code
    claimAt
    activationAt
    maturityAt
    maturityType
    membershipPlan {
      ...MembershipPlan
    }
    state
  }
  ${NOT_COUPON_MEMBERSHIPPLAN_FRAGMENT}
`;

export const ADDRESS_FRAGMENT = gql`
  fragment Address on Address {
    id
    createdAt
    updatedAt
    fullName
    company
    streetLine1
    streetLine2
    city
    province
    postalCode
    country {
      id
      code
      name
    }
    phoneNumber
    defaultShippingAddress
    defaultBillingAddress
    customFields {
      district
    }
  }
`;

export const CUSTOMER_FRAGMENT = gql`
  fragment Customer on Customer {
    id
    createdAt
    updatedAt
    title
    lastName
    phoneNumber
    emailAddress
    availablePoints
    addresses {
      ...Address
    }
    user {
      id
      createdAt
      updatedAt
      identifier
      verified
      lastLogin
      customFields
    }
    distributor {
      id
      name
      phone
    }
    customFields {
      distributor {
        id
        name
        phone
      }
      headPortrait
      gender
      dateBirth
      wechatCode
      isModified
      points
    }
  }
  ${ADDRESS_FRAGMENT}
`;

/**
 * 分销统计
 */
export const DISTRIBUTOR_STATISTICS_FRAGMENT = gql`
  fragment DistributorStatistics on DistributorStatistics {
    effectiveOrderNum
    orderTotal
    accumulationAmount
    orderTotalAmount
    distributorId
    distributorGroupId
    effectiveCustomerNum
    customerCount
    memberCustomerTotalCount
    memberCustomerEffectiveCount
  }
`;

/**
 * 分销订单
 */
export const DISTRIBUTOR_ORDER_FRAGMENT = gql`
  fragment DistributorOrder on DistributorOrder {
    id
    createdAt
    updatedAt
    order {
      customer {
        id
        lastName
        phoneNumber
        customFields {
          headPortrait
        }
      }
      ...Order
    }
  }
  ${ORDER_FRAGMENT}
`;

/** 购物车 */
export const CART_FRAGMENT = gql`
  fragment Cart on Order {
    id
    code
    state
    active
    updatedAt
    orderPlacedAt
    lines {
      ...OrderLine
    }
    totalQuantity
    total
    shipping
    customFields {
      orderPromotionResult {
        promResult {
          orderTotalPrice
        }
      }
    }
  }
  ${ORDER_LINES_FRAGMENT}
`;

/** 优惠换购 */
export const PURCHASE_PREMIUM_FRAGMENT = gql`
  fragment PurchasePremium on PurchasePremium {
    __typename
    id
    name
    validityPeriod {
      type
      startTime
      endTime
      numberOfDays
    }
    state
    minimum
    applicableProduct {
      applicableType
      productIds
    }
    purchasePremiumProducts {
      id
      price
      enabled
      product {
        id
        name
        enabled
        optionGroups {
          id
          code
          name
          customFields {
            priority
          }
          options {
            id
            code
            name
            customFields {
              priority
            }
          }
        }
        variants {
          id
          name
          options {
            code
            name
            id
            group {
              id
              code
              name
            }
          }
          featuredAsset {
            source
          }
          isThereAnyStock
          price
          sku
        }
        featuredAsset {
          source
        }
        customFields {
          price
          markingPrice
        }
      }
    }
    activitySynopsis
    introduce
    activityContent
    activitySuperposition
    promotion {
      id
    }
  }
`;

export const DISCOUNT_ACTIVITY_FRAGMENT = gql`
  fragment DiscountActivity on DiscountActivity {
    __typename
    id
    name
    createdAt
    updatedAt
    remarks
    status
    startTime
    endTime
    minimum
    introduce
    discount
    productIds
    smallProgramQRCodeLink
    activityContent
    activitySynopsis
    activitySuperposition
    showLabelInCommodity
    promotion {
      id
    }
  }
`;

export const FULL_DISCOUNT_PRESENT_FRAGMENT = gql`
  fragment FullDiscountPresent on FullDiscountPresent {
    __typename
    id
    name
    createdAt
    updatedAt
    remarks
    status
    startTime
    endTime
    fullDiscountType: type
    introduce
    ruleType
    showLabelInCommodity
    whetherRestrictUsers
    groupType
    ruleValues {
      minimum
      discountValue {
        discountType
        discount
      }
      freeGiftValues {
        freeGiftId
        maximumOffer
      }
      maximumOffer
    }
    applicableProduct {
      applicableType
      productIds
    }
    fullDiscountContent: activityContent {
      fullMinus
      fullDiscount
      fullPresent
    }
    fullDiscountActivitySynopsis: activitySynopsis {
      synopsisStr
      synopsisTags
    }
    activitySuperposition
    promotion {
      id
    }
    activityGifts {
      id
      name
      description
      enabled
      variants {
        id
        name
        options {
          code
          name
          id
          group {
            id
            code
            name
          }
        }
        featuredAsset {
          id
          preview
          source
        }
        isThereAnyStock
        price
        priceWithTax
        sku
      }
      featuredAsset {
        id
        width
        height
        name
        preview
        source
        type
      }
      customFields {
        price
        markingPrice
        unit
        limitType
        limitCount
        putOnSaleType
        putOnSaleTime
        isVipProduct
      }
    }
  }
`;

export const PACKAGE_DISCOUNT_FRAGMENT = gql`
  fragment PackageDiscount on PackageDiscount {
    __typename
    id
    createdAt
    updatedAt
    name
    displayName
    remarks
    status
    startTime
    endTime
    price
    selectCount
    productIds
    activitySynopsis
    showLabelInCommodity
    activityContentType: activityContent
    promotion {
      id
    }
    activitySuperposition
    statisticsData {
      totalPayment
      totalOrders
      customerCount
      averageOrderValue
    }
  }
`;

export const SELECTIVE_GIFT_ACTIVITY_FRAGMENT = gql`
  fragment SelectiveGiftActivity on SelectiveGiftActivity {
    __typename
    id
    createdAt
    updatedAt
    displayName
    name
    selectiveGiftActivityType: type
    remarks
    status
    startTime
    endTime
    introduce
    ruleType
    ruleValues {
      minimum
      discountValue {
        discountType
        discount
      }
      freeGiftValues {
        freeGiftId
        maximumOffer
      }
      maximumOffer
    }
    applicableProduct {
      applicableType
      productIds
    }
    promotion {
      id
    }
    activityContent
    activitySynopsis
    activitySuperposition
    activityGifts {
      id
      name
      enabled
      variants {
        id
        name
        options {
          code
          name
          id
          group {
            id
            code
            name
          }
        }
        featuredAsset {
          source
        }
        isThereAnyStock
        price
        sku
      }
      featuredAsset {
        source
      }
      customFields {
        price
        markingPrice
        isVipProduct
      }
    }
  }
`;

export const SHOPPING_CREDITS_CLAIM_ACTIVITY_FRAGMENT = gql`
  fragment ShoppingCreditsClaimActivity on ShoppingCreditsClaimActivity {
    __typename
    id
    createdAt
    updatedAt
    name
    remarks
    displayName
    status
    startTime
    endTime
    introduce
    ruleType
    shoppingCreditConditionType: type
    shoppingCreditsClaimActivityRuleValues: ruleValues {
      minimum
      discountValue {
        discountType
        discount
        memberDiscount
      }
      freeGiftValues {
        freeGiftId
        maximumOffer
      }
      maximumOffer
    }
    applicableProduct {
      applicableType
      productIds
    }
    smallProgramQRCodeLink
    promotion {
      id
    }
    activityContent
    shoppingCreditsClaimActivitySynopsis: activitySynopsis {
      synopsisStr
      synopsisTags
    }
    activitySuperposition
  }
`;

export const SHOPPING_CREDITS_DEDUCTION_ACTIVITY_FRAGMENT = gql`
  fragment ShoppingCreditsDeductionActivity on ShoppingCreditsDeductionActivity {
    __typename
    id
    createdAt
    updatedAt
    name
    remarks
    displayName
    status
    startTime
    endTime
    introduce
    minimum
    deductionRate
    memberDeductionRate
    showLabelInCommodity
    applicableProduct {
      applicableType
      productIds
    }
    smallProgramQRCodeLink
    promotion {
      id
    }
    activityContent
    activitySynopsis
    activitySuperposition
  }
`;
